import { App, createApp, h, Plugin, DefineComponent } from 'vue';
import { createInertiaApp } from '@inertiajs/vue3';
import type { InertiaAppProps, InertiaApp } from '@inertiajs/vue3/types/app';
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers';
import { autoAnimatePlugin } from '@formkit/auto-animate/vue';

import { initializeApp } from '@src/Utils/app-init';
import { Modular } from '@/Modular';
import { setupSentry } from '@src/Sentry/Composables/setup';
import.meta.glob(['../images/**']);
import './bootstrap';
import ToastNotifications from './Components/ToastNotifications.vue';
import { useComponents } from './common';
import { lang, langChoice } from './lang/composable';
import { installStaleChunkReload } from '@src/Common/Support/staleChunkReload';

installStaleChunkReload();

// import 'virtual:svg-icons-register';

createInertiaApp({
    /**
     * Inertia progress indicator options
     */
    progress: {
        // The delay after which the progress bar will
        // appear during navigation, in milliseconds.
        delay: 250,

        // The color of the progress bar.
        color: '#fabb30',

        // Whether to include the default NProgress styles.
        includeCSS: false,

        // Whether the NProgress spinner will be shown.
        showSpinner: false,
    },
    /**
     * Get page component from the page name
     *
     * @param name Page name (such as: @Admin::Index)
     * @returns Page component instance
     */
    resolve: (
        name: string,
    ):
        | DefineComponent
        | Promise<DefineComponent>
        | {
              default: DefineComponent;
          } => {
        const matched = /@(.*)::/.exec(name);

        if (matched === null) {
            // When the page is not located in module (such as: @Index)
            return resolvePageComponent(
                `./Pages/${name}.vue`,
                import.meta.glob('./Pages/**/*.vue'),
            ) as Promise<DefineComponent>;
        }

        // Page is located in module (such as: @Admin::Index)
        const module = matched[1];
        const pageName = name.replace(matched[0], '');

        // Contentable custom admin views
        if (module === 'ContentableItem') {
            const component = pageName.split('/');
            component.shift();

            return resolvePageComponent(
                `./Contentable/${pageName}.vue`,
                import.meta.glob('./Contentable/**/*.vue'),
            ).catch(() => {
                return resolvePageComponent(
                    `/src/Contentable/Pages/Items/${component}.vue`,
                    import.meta.glob('/src/Contentable/Pages/**/*.vue'),
                );
            }) as Promise<DefineComponent>;
        }

        return resolvePageComponent(
            `/src/${module}/Pages/${pageName}.vue`,
            import.meta.glob('/src/**/Pages/**/*.vue'),
        ) as Promise<DefineComponent>;
    },
    /**
     * Instantiate Inertia app as Vue plugin
     *
     * @param param Plugin setup params
     */
    setup({ el, App, props, plugin }: { el: Element; App: InertiaApp; props: InertiaAppProps; plugin: Plugin }) {
        const app: App<Element> = createApp({ render: () => h(App, props) });

        // Sentry
        setupSentry(app);

        // Localization
        initializeApp(props.initialPage.props);

        // Inertia
        app.use(plugin);

        // Plugins
        app.use(autoAnimatePlugin);

        // Mixins
        app.mixin({ methods: { route, lang, langChoice } });

        // Register common components
        useComponents(app);

        // Run modules setups
        new Modular(app);

        // Plugin components
        app.component('ToastNotifications', ToastNotifications);

        app.mount(el);
    },
});
